import React from 'react';
import { BuilderIcon } from '../../shell/icons';
import { SleeperAvatar, PlayerHeadshot } from '../../shell/SleeperAvatar';
import { formatPickDisplay, ordinal } from './rmtContext';

/**
 * TradeSides — the two "gives" columns of a Rate My Trade post, shell-styled.
 * Shared by the feed, detail, and create surfaces. Pass `teamA`/`teamB`
 * (cachedTeamData entries) for full team context — avatar, team name, owner,
 * record (previous season when the current one is 0-0). The detail page adds
 * `metaA`/`metaB` ({ rank, totalTeams, recordSeason }) and `onOwner` to make
 * owner names link out to Manager Intel.
 */

const recordLine = (team, recordSeason) => {
  if (!team) return null;
  const fmt = (r) => (r && (r.wins || r.losses) ? `${r.wins}-${r.losses}${r.ties ? `-${r.ties}` : ''}` : null);
  const current = fmt(team.record);
  const prev = fmt(team.prevRecord);
  if (current) return current;
  if (prev) return recordSeason ? `${prev} in ${recordSeason}` : `${prev} last season`;
  return null;
};

/* Identity rule: the manager username leads the header; the team name and
   record sit on the secondary line. The Manager Intel click-through lives
   on the username itself. */
const SideHeader = ({ label, team, meta, onOwner }) => {
  if (!team) return <span className="ft-k">{label}</span>;
  const record = recordLine(team, meta?.recordSeason);
  const sub = [team.teamName, record].filter(Boolean).join(' · ');
  const clickable = !!(onOwner && team.ownerName);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
      <SleeperAvatar id={team.avatar} name={team.ownerName || team.teamName} size={30} radius="50%" />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span
            onClick={clickable ? (e) => { e.stopPropagation(); onOwner(team.ownerName); } : undefined}
            role={clickable ? 'link' : undefined}
            style={clickable ? {
              cursor: 'pointer', textDecoration: 'underline',
              textDecorationColor: 'var(--ft-hairline)', textUnderlineOffset: 2,
            } : undefined}
          >
            {team.ownerName || team.teamName}
          </span>
          {meta?.rank ? (
            <span style={{
              fontSize: 10, fontWeight: 700, color: 'var(--ft-ink-3)', background: 'var(--ft-surface)',
              padding: '1px 7px', borderRadius: 6, marginLeft: 7, verticalAlign: '1px',
            }}>
              {ordinal(meta.rank)} of {meta.totalTeams}
            </span>
          ) : null}
        </div>
        {sub ? (
          <div style={{ fontSize: 11, color: 'var(--ft-ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const SideList = ({ label, team, side, sends, meta, onOwner }) => (
  <div style={{ minWidth: 0 }}>
    <SideHeader label={label} team={team} meta={meta} onOwner={onOwner} />
    {team ? <span className="ft-k" style={{ display: 'block', marginTop: 10 }}>{sends || 'Sends'}</span> : null}
    <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
      {(side?.players || []).map((p) => (
        <li key={p.id || p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 500, padding: '3px 0' }}>
          {p.id ? <PlayerHeadshot playerId={p.id} size={24} /> : null}
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.name}
            {p.position ? <span style={{ color: 'var(--ft-ink-3)', fontWeight: 400, fontSize: 11.5, marginLeft: 5 }}>{p.position}{p.team ? ` · ${p.team}` : ''}</span> : null}
          </span>
        </li>
      ))}
      {(side?.picks || []).map((pk) => (
        <li key={pk.id || pk.displayName} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 500, padding: '3px 0' }}>
          <span style={{
            width: 24, height: 24, borderRadius: '50%', background: 'var(--ft-surface-hover)', flex: 'none',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 700, color: 'var(--ft-ink-2)',
          }}>
            {pk.round ? `R${pk.round}` : 'PK'}
          </span>
          {formatPickDisplay(pk) || `${pk.season} ${ordinal(pk.round || 1)}`}
        </li>
      ))}
      {(side?.players || []).length === 0 && (side?.picks || []).length === 0 ? (
        <li style={{ fontSize: 13, color: 'var(--ft-ink-3)' }}>Nothing</li>
      ) : null}
    </ul>
  </div>
);

/* Direction rule (2026-06-12): every side shows what that party RECEIVES —
   matching the trade canvas. Column A = team A's haul (team B's gives). */
const TradeSides = ({ trade, labelA, labelB, teamA, teamB, metaA, metaB, onOwner }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 26px 1fr', gap: 10, alignItems: 'start' }}>
    <SideList label={labelA || 'Side A receives'} team={teamA} side={trade.teamBGives} sends="Receives" meta={metaA} onOwner={onOwner} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 22, color: 'var(--ft-ink-3)' }}>
      <BuilderIcon style={{ width: 16, height: 16 }} />
    </div>
    <SideList label={labelB || 'Side B receives'} team={teamB} side={trade.teamAGives} sends="Receives" meta={metaB} onOwner={onOwner} />
  </div>
);

export default TradeSides;
